"use client";

import * as React from "react";
import { derivePalette, type BrandPalette } from "./derive-palette";

interface BrandingContextValue {
  hex: string;
  palette: BrandPalette;
  setHex: (next: string) => void;
}

const Ctx = React.createContext<BrandingContextValue | null>(null);

export interface BrandingProviderProps {
  children: React.ReactNode;
  /** Initial brand hex; falls back to `#1d6fe5` */
  initialHex?: string;
}

export function BrandingProvider({ children, initialHex = "#1d6fe5" }: BrandingProviderProps) {
  const [hex, setHex] = React.useState(initialHex);
  const palette = React.useMemo(() => derivePalette(hex), [hex]);

  React.useEffect(() => {
    const root = document.documentElement;
    // Apply only the brand-specific overrides (do not touch core tokens)
    root.style.setProperty("--brand-accent", palette.accent);
    root.style.setProperty("--brand-accent-fg", palette.accentFg);
    root.style.setProperty("--brand-accent-strong", palette.accentStrong);
  }, [palette]);

  const value = React.useMemo(() => ({ hex, palette, setHex }), [hex, palette]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBranding(): BrandingContextValue {
  const c = React.useContext(Ctx);
  if (!c) throw new Error("useBranding must be used inside <BrandingProvider>");
  return c;
}
